/* eslint-disable no-empty */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { decrease, increase } from '../features/loading/loadingSlice';
import { CaseSource, SearchResult } from './api';
import { CaseClass, Diary, OccurrenceType, SelectOption } from './byggrApi';
import { ArchivedDocument, Case, CasePerson, Occurrence, Preview } from './casesApi';
import { Config } from './configsApi';
import { Position } from './mapApi';

// Define a service using a base URL and expected endpoints
export const api = createApi({
  baseQuery: fetchBaseQuery({ baseUrl: 'api' }),
  keepUnusedDataFor: 3600,
  endpoints: (builder) => ({
    getCaseClassesWithoutDiary: builder.query<CaseClass[], void>({
      query: () => 'byggr/caseClassesWithoutDiary'
    }),
    getDiaries: builder.query<Diary[], void>({
      query: () => 'byggr/diaries'
    }),
    getOccurrenceTypes: builder.query<OccurrenceType[], void>({
      query: () => 'byggr/occurrenceTypes'
    }),
    getDocumentStatuses: builder.query<SelectOption[], void>({
      query: () => 'byggr/documentStatuses'
    }),
    getDocumentTypes: builder.query<SelectOption[], void>({
      query: () => 'byggr/documentTypes'
    }),
    getRoles: builder.query<SelectOption[], void>({
      query: () => 'byggr/roles'
    }),
    getConfigs: builder.query<Config[], void | null>({
      query: () => 'configs',
      forceRefetch: () => true
    }),
    getPersons: builder.query<unknown[], { estateId: string, distance: number; }>({
      query: ({ estateId, distance }) => `persons?estateId=${estateId}&distance=${distance}`,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          dispatch(increase());
          await queryFulfilled;
        } catch { } finally {
          dispatch(decrease());
        }
      }
    }),
    getOwners: builder.query<unknown[], { estateId: string, distance: number; }>({
      query: ({ estateId, distance }) => `owners?estateId=${estateId}&distance=${distance}`,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          dispatch(increase());
          await queryFulfilled;
        } catch { } finally {
          dispatch(decrease());
        }
      }
    }),
    search: builder.query<SearchResult[], { query: string, configId: string, source?: CaseSource | 'FB'; }>({
      query: ({ query, configId, source }) => {
        let url = `search?query=${query}&configId=${configId}`;
        if (source) {
          url = `${url}&source=${source}`;
        }
        return url;
      },
    }),
    getCases: builder.query<Case[], { searchResult: SearchResult, configId: string; }>({
      query: ({ searchResult, configId }) => {
        let url;
        if (searchResult.type === 'Fastighet' || searchResult.type === 'Adress') {
          url = `cases/${searchResult.value}`;
          if (searchResult.estateName) {
            url = `${url}/${searchResult.estateName}`;
          }
          if (configId) {
            url = `${url}?configId=${configId}`;
          }
          return url;
        }
        else {
          if (searchResult.value === '00000000-0000-0000-0000-000000000000') {
            url = `cases?caseId=${searchResult.displayName}&caseSource=${searchResult.source}`;
          }
          else {
            url = `cases?caseId=${searchResult.value}&caseSource=${searchResult.source}`;
          }
          if (searchResult.caseSourceId) {
            url = `${url}&caseSourceId=${searchResult.caseSourceId}`;
          }
          if (configId) {
            url = `${url}&configId=${configId}`;
          }
          return url;
        }
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          dispatch(increase());
          await queryFulfilled;
        } catch { } finally {
          dispatch(decrease());
        }
      }
    }),
    getCapabilities: builder.query<any, string>({
      query: (url) => ({ url, responseHandler: "text", cache: "no-store" })
    }),
    getEstateName: builder.query<string, number | string>({
      query: (fnr) => ({
        url: `map/estateName?fnr=${fnr}`,
        responseHandler: "text"
      })
    }),
    getEstatePosition: builder.query<Position, number | string>({
      query: (fnr) => `map/estatePosition?fnr=${fnr}`
    }),
    getGeometryFromFnr: builder.query<any, number | string>({
      query: (fnr) => `map/geometry?fnr=${fnr}`
    }),
    getPreview: builder.query<Preview, { caseId: string, caseSource: string, caseSourceId: string, configId: string; }>({
      query: ({ caseId, caseSource, caseSourceId, configId }) => `cases/${caseId}/preview?caseSourceId=${caseSourceId}&caseSource=${caseSource}&configId=${configId}`
    }),
    getOccurrences: builder.query<Occurrence[], { caseId: string, caseSource: string, caseSourceId: string, configId: string; }>({
      query: ({ caseId, caseSource, caseSourceId, configId }) => `cases/${caseId}/occurrences?caseSourceId=${caseSourceId}&caseSource=${caseSource}&configId=${configId}`
    }),
    getCasePersons: builder.query<CasePerson[], { caseId: string, caseSource: string, caseSourceId: string, configId: string; }>({
      query: ({ caseId, caseSource, caseSourceId, configId }) => `cases/${caseId}/persons?caseSourceId=${caseSourceId}&caseSource=${caseSource}&configId=${configId}`
    }),
    getArchivedDocuments: builder.query<ArchivedDocument[], { caseId: string, caseSource: string, caseSourceId: string, configId: string; }>({
      query: ({ caseId, caseSource, caseSourceId, configId }) => `cases/${caseId}/archivedDocuments?caseSourceId=${caseSourceId}&caseSource=${caseSource}&configId=${configId}`
    })
  })
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetCaseClassesWithoutDiaryQuery,
  useGetDiariesQuery,
  useGetOccurrenceTypesQuery,
  useGetDocumentStatusesQuery,
  useGetDocumentTypesQuery,
  useGetRolesQuery,
  useGetConfigsQuery,
  useGetPersonsQuery,
  useGetOwnersQuery,
  useGetCasesQuery,
  useLazyGetCasesQuery,
  useLazyGetCapabilitiesQuery,
  useLazyGetEstateNameQuery,
  useLazyGetEstatePositionQuery,
  useLazyGetGeometryFromFnrQuery,
  useLazyGetPreviewQuery,
  useLazyGetOccurrencesQuery,
  useLazyGetCasePersonsQuery,
  useLazyGetArchivedDocumentsQuery,
  useLazySearchQuery,
  usePrefetch
} = api;
